/**
 * JoinRequests.jsx
 * Admin panel — review join requests with Accept/Decline.
 * Accept opens PermissionsModal before adding the user.
 */
import React, { useState, useEffect, useCallback } from "react";
import PermissionsModal from "./PermissionsModal";

const BASE_URL = process.env.REACT_APP_API_URL || "";

const fmtDate = (d) => d
  ? new Date(d).toLocaleString("en-PK", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

export default function JoinRequests({ business, currentUser, onMemberAdded }) {
  const [requests,    setRequests]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState("pending");
  const [processing,  setProcessing]  = useState(null);
  const [declineConfirm, setDeclineConfirm] = useState(null);
  const [permRequest, setPermRequest] = useState(null); // request awaiting permission modal
  const [error,       setError]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/api/businesses/${business.businessId}/requests`);
      const data = await res.json();
      setRequests(data.requests || []);
    } catch { setError("Could not load requests."); }
    finally { setLoading(false); }
  }, [business.businessId]);

  useEffect(() => { load(); }, [load]);

  const filtered = requests.filter((r) => filter === "all" || r.status === filter);

  // ── Accept with permissions ─────────────────────────────────────────────────
  const handleAcceptWithPermissions = async (permissions) => {
    if (!permRequest) return;
    setProcessing(permRequest.requestId);
    setError("");
    try {
      const res  = await fetch(`${BASE_URL}/api/businesses/requests/${permRequest.requestId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "accept", reviewedBy: currentUser, permissions }),
      });
      const data = await res.json();
      if (data.success) {
        setRequests((prev) => prev.map((r) =>
          r.requestId === permRequest.requestId
            ? { ...r, status: "accepted", reviewedBy: currentUser, reviewedAt: new Date().toISOString() }
            : r
        ));
        if (onMemberAdded) onMemberAdded(data.message);
      } else { setError(data.message || "Failed to accept request."); }
    } catch { setError("Network error."); }
    finally { setProcessing(null); setPermRequest(null); }
  };

  // ── Decline ─────────────────────────────────────────────────────────────────
  const handleDecline = async (request) => {
    setProcessing(request.requestId);
    setError("");
    try {
      const res  = await fetch(`${BASE_URL}/api/businesses/requests/${request.requestId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "decline", reviewedBy: currentUser }),
      });
      const data = await res.json();
      if (data.success) {
        setRequests((prev) => prev.map((r) =>
          r.requestId === request.requestId
            ? { ...r, status: "declined", reviewedBy: currentUser, reviewedAt: new Date().toISOString() }
            : r
        ));
      } else { setError(data.message || "Failed to decline."); }
    } catch { setError("Network error."); }
    finally { setProcessing(null); setDeclineConfirm(null); }
  };

  const STATUS = {
    pending:  { bg: "rgba(212,168,83,0.15)",  color: "var(--accent-gold)",  icon: "⏳" },
    accepted: { bg: "rgba(81,207,102,0.12)",  color: "var(--accent-green)", icon: "✅" },
    declined: { bg: "rgba(255,107,107,0.12)", color: "var(--accent-rose)",  icon: "❌" },
  };

  return (
    <div>
      {/* Permissions modal */}
      {permRequest && (
        <PermissionsModal
          request={permRequest}
          onConfirm={handleAcceptWithPermissions}
          onCancel={() => setPermRequest(null)}
          loading={!!processing}
        />
      )}

      {/* Decline confirm modal */}
      {declineConfirm && (
        <>
          <div onClick={() => setDeclineConfirm(null)} style={{ position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.65)", zIndex: 500, backdropFilter: "blur(4px)" }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            zIndex: 501, width: "min(400px,92vw)", background: "var(--bg-card)",
            border: "1px solid var(--border)", borderRadius: "var(--radius-xl)",
            boxShadow: "0 24px 80px rgba(0,0,0,0.6)", animation: "fadeIn 0.2s ease", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", background: "rgba(255,107,107,0.08)", borderBottom: "1px solid var(--border)" }}>
              <h3 style={{ fontSize: 18, marginBottom: 4 }}>❌ Decline Request</h3>
              <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                {declineConfirm.username}'s request to join {declineConfirm.businessName} will be declined.
              </p>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", gap: 10 }}>
              <button className="btn" disabled={!!processing}
                onClick={() => handleDecline(declineConfirm)}
                style={{ flex: 1, padding: "12px", fontSize: 14, fontWeight: 700,
                  background: "rgba(255,107,107,0.15)", border: "1px solid rgba(255,107,107,0.3)", color: "var(--accent-rose)" }}>
                {processing ? "Declining…" : "❌ Confirm Decline"}
              </button>
              <button className="btn btn-ghost" onClick={() => setDeclineConfirm(null)}
                style={{ flex: 1, padding: "12px", fontSize: 14 }}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 22 }}>
        {[
          { label: "Pending",  count: requests.filter(r=>r.status==="pending").length,  key:"pending",  color:"rgba(212,168,83,0.15)",  icon:"⏳" },
          { label: "Accepted", count: requests.filter(r=>r.status==="accepted").length, key:"accepted", color:"rgba(81,207,102,0.12)",  icon:"✅" },
          { label: "Declined", count: requests.filter(r=>r.status==="declined").length, key:"declined", color:"rgba(255,107,107,0.12)", icon:"❌" },
        ].map((s) => (
          <div key={s.key} onClick={() => setFilter(s.key)} className="card"
            style={{ padding:"16px 20px", cursor:"pointer", display:"flex", alignItems:"center", gap:12,
              border: filter===s.key ? "1px solid var(--border-accent)" : "1px solid var(--border)",
              background: filter===s.key ? s.color : "var(--bg-card)", transition:"all 0.2s" }}>
            <span style={{ fontSize:24 }}>{s.icon}</span>
            <div>
              <p style={{ fontSize:11, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:500 }}>{s.label}</p>
              <p style={{ fontSize:22, fontFamily:"var(--font-display)", fontWeight:700 }}>{s.count}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {[["pending","⏳ Pending"],["accepted","✅ Accepted"],["declined","❌ Declined"],["all","All"]].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)} className="btn" style={{
            padding:"6px 14px", fontSize:12,
            background: filter===k ? "var(--accent-gold-dim)" : "transparent",
            border: filter===k ? "1px solid var(--border-accent)" : "1px solid var(--border)",
            color: filter===k ? "var(--accent-gold)" : "var(--text-secondary)", fontWeight: filter===k ? 600 : 400,
          }}>{l}</button>
        ))}
        <button onClick={load} className="btn btn-ghost" style={{ marginLeft:"auto", padding:"6px 12px", fontSize:12 }}>🔄 Refresh</button>
      </div>

      {error && <div style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.3)",
        borderRadius:8, padding:"10px 14px", color:"var(--accent-rose)", fontSize:13, marginBottom:14 }}>❌ {error}</div>}

      {/* Request list */}
      {loading ? (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[...Array(3)].map((_,i) => <div key={i} className="skeleton" style={{ height:88, borderRadius:12 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding:48, textAlign:"center", color:"var(--text-muted)" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>{filter==="pending" ? "🎉" : "📭"}</div>
          <p>{filter==="pending" ? "No pending requests — all caught up!" : `No ${filter} requests`}</p>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {filtered.map((r) => {
            const st = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.requestId} className="card"
                style={{ padding:"18px 22px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap",
                  borderColor: r.status==="pending" ? "rgba(212,168,83,0.25)" : "var(--border)" }}>

                {/* Avatar */}
                <div style={{ width:46, height:46, borderRadius:"50%", flexShrink:0,
                  background: r.status==="pending" ? "rgba(212,168,83,0.12)" : "var(--bg-secondary)",
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:20,
                  border:"2px solid "+(r.status==="pending"?"rgba(212,168,83,0.3)":"var(--border)") }}>👤</div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3, flexWrap:"wrap" }}>
                    <span style={{ fontSize:15, fontWeight:700 }}>{r.username}</span>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:99,
                      background:st.bg, color:st.color, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                      {st.icon} {r.status}
                    </span>
                  </div>
                  <p style={{ fontSize:12, color:"var(--text-secondary)" }}>
                    Wants to join <strong>{r.businessName}</strong>
                  </p>
                  <p style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>
                    📅 {fmtDate(r.requestedAt)}
                    {r.reviewedBy && <> · Reviewed by <strong>{r.reviewedBy}</strong> on {fmtDate(r.reviewedAt)}</>}
                  </p>
                </div>

                {/* Actions — pending only */}
                {r.status === "pending" && (
                  <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                    <button className="btn" disabled={processing===r.requestId}
                      onClick={() => setPermRequest(r)}
                      style={{ padding:"9px 20px", fontSize:13, fontWeight:700,
                        background:"rgba(81,207,102,0.15)", border:"1px solid rgba(81,207,102,0.35)",
                        color:"var(--accent-green)" }}>
                      ✅ Accept
                    </button>
                    <button className="btn" disabled={processing===r.requestId}
                      onClick={() => setDeclineConfirm(r)}
                      style={{ padding:"9px 20px", fontSize:13, fontWeight:700,
                        background:"rgba(255,107,107,0.12)", border:"1px solid rgba(255,107,107,0.25)",
                        color:"var(--accent-rose)" }}>
                      ❌ Decline
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
